<script setup lang="ts">
import UiDropdown, { type DropdownItem, type StatusMessage as DropdownStatusMessage } from "~/components/ui/Dropdown.vue";
import UiTextInput, { type StatusMessage as InputStatusMessage } from "~/components/ui/TextInput.vue";
import UiButton from "~/components/ui/Button.vue";
import UiTextarea, { type StatusMessage as TextareaStatusMessage } from "~/components/ui/Textarea.vue";
import LayoutSection from "~/components/layout/Section.vue";
import {ref, watch} from "vue";
import {z} from "zod";

const props = defineProps({
  servicesDropdownItems: {
    type: Array as () => DropdownItem[],
    default: () => [],
  }
});

const contactFormLoading = ref<boolean>(false);
const contactForm = ref<{ fullName: string; serviceType: DropdownItem | null; phone: string; email: string; message: string; privacyPolicyAccepted: boolean }>({
  fullName: "",
  serviceType: null,
  phone: "",
  email: "",
  message: "",
  privacyPolicyAccepted: false
});
const contactFormStatus = ref<{ fullName?: InputStatusMessage; serviceType?: DropdownStatusMessage; phone?: InputStatusMessage; email?: InputStatusMessage; message?: TextareaStatusMessage; form?: InputStatusMessage }>({});
const contactFormSchema = z.object({
  fullName: z
      .string()
      .trim()
      .min(1, { message: "Toto pole je povinné" }),

  serviceType: z
      .object({
        label: z.string(),
        value: z.any(),
      })
      .nullable(),

  phone: z
      .string()
      .trim()
      .refine((val: string) => val === "" || /^\+?[0-9\s\-]{7,20}$/.test(val), { message: "Neplatné telefonní číslo" }),


  email: z
      .string()
      .trim()
      .min(1, { message: "Toto pole je povinné" })
      .email({ message: "Neplatný e-mail" }),

  message: z
      .string()
      .trim()
      .min(1, { message: "Toto pole je povinné" })
});

const checkContactForm = (): boolean => {
  contactFormStatus.value.form = undefined;
  const result = contactFormSchema.safeParse(contactForm.value);

  if (result.success) {
    contactFormStatus.value = {};
    return true;
  }

  const errors: any = {};

  result.error.issues.forEach((issue: z.ZodIssue) => {
    const field: PropertyKey | undefined = issue.path[0];

    if (typeof field !== "string") return;

    errors[field] = { type: "error", message: issue.message };
  });

  contactFormStatus.value = errors;

  return false;
};

const submitContactForm = async () => {
  const isValid: boolean = checkContactForm();

  if (!contactForm.value.privacyPolicyAccepted) {
    contactFormStatus.value.form = { type: "error", message: "Musíte souhlasit se zpracováním osobních údajů." };
    return;
  }

  if (isValid) {
    contactFormLoading.value = true;

    await $fetch("/api/sendContactEmail", {
      method: "POST",
      body: {
        fullName: contactForm.value.fullName,
        serviceType: contactForm.value.serviceType?.value || "",
        phone: contactForm.value.phone || "",
        email: contactForm.value.email,
        message: contactForm.value.message
      },
      ignoreResponseError: true,
      onResponse({ response }: any) {
        if (response.status === 429) {
          contactFormStatus.value.form = {type: "error", message: "Příliš mnoho požadavků. Zkuste to prosím později." };
          return;
        }

        const resCode: string = response._data.resCode.toString();

        switch (resCode) {
          case "1010":
            contactFormStatus.value.form = {type: "error", message: "Některá pole nebyla vyplněna." };
            break;
          case "1020":
            contactFormStatus.value.form = {type: "error", message: "Chyba při odesílání zprávy." };
            break;
          case "1031":
            contactFormStatus.value.form = {type: "success", message: "Zpráva byla úspěšně odeslána." };
            break;
          case "1040":
            contactFormStatus.value.form = {type: "error", message: "Funkce odesílání je momentálně nedostupná." };
            break;
          default:
            contactFormStatus.value.form = {type: "error", message: "Nastala neznámá chyba." };
            break;
        }
      }
    }).then((): void => {
      contactFormLoading.value = false;

      setTimeout((): void => {
        contactFormStatus.value.form = undefined;
      }, 8000);
    })
  }
};

watch(() => contactForm.value, (): void => {
  checkContactForm();
}, { deep: true });
</script>

<template>
  <LayoutSection id="contact" :is-padded-x="false">
    <LayoutSection id="contact-info" background="/images/section-background.jpg">
      <div class="group">
        <div class="section-head">
          <span>Kontakt</span>
          <h2>Kontakt a poptávka služeb</h2>
          <p>
            Máte zájem o naše služby nebo se chcete na něco doptat?
            <br>
            Napište nám prostřednictvím kontaktního formuláře nebo nás kontaktujte telefonicky či e-mailem.
          </p>
        </div>

        <div class="info">
          <div class="column">
            <h3>Kontakt</h3>
            <span class="line"></span>
            <ul>
              <li>
                <NuxtLink to="/">
                  <Icon name="mdi:phone" class="icon" />
                  +420 605 583 489
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/">
                  <Icon name="mdi:email" class="icon" />
                  palkajaro@seznam.cz
                </NuxtLink>
              </li>
            </ul>
          </div>

          <div class="column">
            <h3>Informace</h3>
            <span class="line"></span>
            <ul>
              <li>
                <NuxtLink to="/">
                  <span class="icon ico">IČO</span>
                  653 685 33
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/">
                  <Icon name="material-symbols:pin-drop-rounded" />
                  Záhumní 1105, 691 02 Velké Bílovice
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </LayoutSection>

    <LayoutSection id="contact-form" :is-padded-y="false">
      <div class="img-box">
        <img src="/images/projects/image1.jpg" alt="O nás 2" loading="lazy" />
      </div>

      <form @submit.prevent="submitContactForm">
        <div class="line">
          <UiTextInput :status-message="contactFormStatus.fullName" v-model="contactForm.fullName" label="* Celé jméno" id="full-name" name="full-name" placeholder="Jan Novák" required />
          <UiDropdown :status-message="contactFormStatus.serviceType" v-model="contactForm.serviceType" label="Typ služby" id="service-type" name="service-type" max-height="140" :items="props.servicesDropdownItems"/>
        </div>

        <div class="line">
          <UiTextInput :status-message="contactFormStatus.phone" v-model="contactForm.phone" label="Telefonní číslo" id="phone" name="phone" placeholder="+420 123 456 789" />
          <UiTextInput :status-message="contactFormStatus.email" v-model="contactForm.email" label="* E-mail" id="email" name="email" placeholder="jan.novak@gmail.com" required />
        </div>

        <UiTextarea :status-message="contactFormStatus.message" v-model="contactForm.message" label="* Zpráva" id="message" placeholder="Dobrý den, ..." required></UiTextarea>

        <div class="footer">
          <div class="privacy-policy-consent">
            <input v-model="contactForm.privacyPolicyAccepted" type="checkbox" id="privacy-policy-input" name="privacy-policy" required />
            <label for="privacy-policy-input">Souhlasím se zpracováním osobních údajů dle <NuxtLink to="/privacy-policy">zásad ochrany osobních údajů</NuxtLink>.</label>
          </div>

          <p>Pole označená * jsou povinná</p>
          <UiButton type="primary" variant="solid" trailing-icon="material-symbols:arrow-right-rounded" :loading="contactFormLoading">Odeslat</UiButton>
          <p class="status-message" :class="[contactFormStatus.form?.type]" v-if="contactFormStatus.form">{{ contactFormStatus.form.message }}</p>
        </div>
      </form>
    </LayoutSection>
  </LayoutSection>
</template>

<style scoped lang="scss">
#contact {
  display: flex;
  gap: 60px;
  flex-direction: column;
  padding-top: 0;

  #contact-info .group {
    display: flex;
    flex-direction: row;
    gap: 60px;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;

    .section-head {
      flex: 1;
    }

    .info {
      display: flex;
      flex-direction: row;
      gap: 60px;
      justify-content: flex-start;
      flex-wrap: wrap;

      .column {
        display: flex;
        flex-direction: column;
        gap: 20px;

        h3 {
          font-size: 23px;
          font-weight: 700;
          color: rgb(var(--title-light-color));
        }

        .line {
          width: 50px;
          height: 2px;
          background: rgb(var(--main-color));
          border-radius: var(--small-border-radius);
        }

        ul {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 20px;

          li a {
            display: flex;
            align-items: center;
            gap: 10px;
            text-decoration: none;
            font-size: 18px;
            color: rgb(var(--text-light-color));
            transition: 0.2s;

            &:hover {
              color: rgb(var(--mini-title-light-color));
            }

            .icon {
              font-size: 21px;
              line-height: 0;

              &.ico {
                font-size: 10px;
                font-weight: 700;
              }
            }
          }
        }
      }
    }
  }

  #contact-form {
    display: flex;
    flex-direction: row;
    gap: 60px;
    flex-wrap: wrap;

    .img-box {
      display: flex;
      width: 100%;
      border-radius: var(--large-border-radius);
      overflow: hidden;
      position: relative;
      min-height: 200px;
      min-width: 250px;
      flex: 1;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
      }
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 30px;
      flex: 1;

      .line {
        display: flex;
        flex-direction: row;
        gap: 30px;
        flex-wrap: wrap;
        flex: 1;

        ::v-deep(.ui-input) {
          flex: 1;
        }

        ::v-deep(.ui-dropdown) {
          flex: 1;
          height: fit-content;
        }
      }

      ::v-deep(.ui-textarea textarea) {
        min-height: 150px;
        max-height: 250px;
      }

      .footer {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 30px;

        .privacy-policy-consent {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 10px;
          color: rgb(var(--text-light-color));

          #privacy-policy-input {
            width: 15px;
            height: 15px;
            accent-color: rgb(var(--main-color));
          }

          a {
            color: rgb(var(--btn-primary-background));
            text-decoration: none;
            transition: 0.2s;

            &:hover {
              color: rgb(var(--btn-primary-hover-background));
            }
          }
        }

        p {
          font-size: 15px;
          color: rgb(var(--text-light-color));

          &.status-message {
            width: 100%;

            &.error {
              color: rgb(var(--error-color));
            }

            &.warning {
              color: rgb(var(--warning-color));
            }

            &.success {
              color: rgb(var(--success-color));
            }

            &.info {
              color: rgb(var(--info-color));
            }
          }
        }
      }
    }
  }
}

@media (max-width: 1225px) {
  #contact .group  {
    flex-direction: column;
    align-items: flex-start;
    gap: 30px;
  }
}
</style>